import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, UploadIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/popover.tsx';
import { cn } from '@r/utils/utils.ts';
import { Calendar } from '@ui/calendar.tsx';
import type { FC } from 'react';
import { useRef, useEffect } from 'react';
import { toUtcStartOfDay, toUtcEndOfDay } from '@r/utils/date-utils.ts';
import { useApiAdmingamesIdPatch, apiMediasPost } from '@r/api/generated/client.ts';
import type {
  GameGameReadModel,
  GameGameUpdateJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { EntitySegmentsCard } from '@admin/components/segments/entity-segments-card.tsx';

type Game = GameGameReadModel & {
  createdBy?: { user: { fullname: string } };
};

interface UpdateGameDialogProps {
  game: Game | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const getUpdateGameSchema = (t: (key: string) => string, status?: string) => {
  const isPlanned = status === 'game.status.planned';
  const isActive = status === 'game.status.active';

  const baseSchema = z.object({
    label: isPlanned
      ? z.string().min(1, t('games.create.label.required')).max(100)
      : z.string().max(100).optional(),
    description: isPlanned ? z.string().optional() : z.string().optional(),
    openingDate: isPlanned
      ? z.date({
          required_error: t('games.create.opening.date.required')
        })
      : z.date().optional(),
    closingDate:
      isPlanned || isActive
        ? z.date({
            required_error: t('games.create.closing.date.required')
          })
        : z.date().optional(),
    link: isPlanned
      ? z.string().url(t('games.create.link.invalid')).min(1, t('games.create.link.required'))
      : z.string().url(t('games.create.link.invalid')).optional(),
    cover: z
      .instanceof(File, { message: t('games.create.cover.type.invalid') })
      .refine((file) => file.size <= 5 * 1024 * 1024, {
        message: t('games.create.cover.size.max')
      })
      .refine((file) => file.type.startsWith('image/'), {
        message: t('games.create.cover.type.invalid')
      })
      .optional()
  });

  // baseSchema est aussi renvoyé car <Form schema> attend un ZodObject pour afficher les champs requis
  if (isPlanned) {
    const schema = baseSchema.refine(
      (data) => {
        if (!data.closingDate || !data.openingDate) return true;
        return data.closingDate > data.openingDate;
      },
      {
        message: t('games.create.dates.invalid'),
        path: ['closingDate']
      }
    );
    return { baseSchema, schema };
  }

  return { baseSchema, schema: baseSchema };
};

export const UpdateGameDialog: FC<UpdateGameDialogProps> = ({
  game,
  isOpen,
  onOpenChange,
  onSuccess
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPlanned = game?.status === 'game.status.planned';
  const isActive = game?.status === 'game.status.active';

  const canEditLabel = isPlanned;
  const canEditDescription = isPlanned || isActive;
  const canEditOpeningDate = isPlanned;
  const canEditClosingDate = isPlanned || isActive;
  const canEditLink = isPlanned;
  const canEditCover = isPlanned || isActive;

  const { baseSchema, schema: updateGameSchema } = getUpdateGameSchema(t, game?.status);

  const form = useForm<z.infer<typeof updateGameSchema>>({
    resolver: zodResolver(updateGameSchema),
    defaultValues: {
      label: '',
      description: '',
      openingDate: undefined,
      closingDate: undefined,
      link: '',
      cover: undefined
    }
  });

  useEffect(() => {
    if (game && isOpen) {
      const linkValue = typeof game.link === 'string' ? game.link : String(game.link ?? '');
      form.reset({
        label: game.label,
        description: game.description || '',
        openingDate: game.openingDate ? new Date(game.openingDate) : undefined,
        closingDate: game.closingDate ? new Date(game.closingDate) : undefined,
        link: linkValue,
        cover: undefined
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [game, isOpen, form]);

  const openingDate = form.watch('openingDate');
  const closingDate = form.watch('closingDate');

  const updateGameMutation = useApiAdmingamesIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('games.update.success'));
        onOpenChange(false);
        onSuccess();
      },
      onError: (error: unknown) => {
        if (error instanceof Error && !('response' in error)) {
          toast.error(error.message);
          return;
        }

        if (isOrvalErrorFromPath(error, 'patch', '/admin/games')) {
          showOrvalErrorToast(error);
        } else {
          toast.error(t('games.update.error'));
        }
      }
    }
  });

  const handleSubmit = async (data: z.infer<typeof updateGameSchema>) => {
    if (!game) throw new Error('Game is required');

    let coverIri: string | undefined;

    if (canEditCover && data.cover) {
      try {
        const media = await apiMediasPost({
          file: data.cover,
          type: 'layout'
        });
        coverIri = media.iri;
      } catch (uploadError: unknown) {
        showOrvalErrorToast(uploadError, t('games.create.cover.upload.error'));
        return;
      }
    }

    const patchData: GameGameUpdateJsonMergePatchModel = {};

    if (canEditLabel && data.label) {
      patchData.label = data.label;
    }
    if (canEditDescription) {
      patchData.description = data.description || null;
    }
    if (canEditOpeningDate && data.openingDate) {
      patchData.openingDate = toUtcStartOfDay(data.openingDate).toISOString();
    }
    if (canEditClosingDate && data.closingDate) {
      patchData.closingDate = toUtcEndOfDay(data.closingDate).toISOString();
    }
    if (canEditLink && data.link) {
      patchData.link = data.link;
    }
    if (canEditCover && coverIri) {
      patchData.cover = coverIri;
    }

    updateGameMutation.mutate({ id: game.id.toString(), data: patchData });
  };

  function onSubmit(data: z.infer<typeof updateGameSchema>) {
    handleSubmit(data);
  }

  if (!game) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-14 max-w-2xl max-h-[90vh] overflow-y-auto scrollable">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('games.update.title')}</DialogTitle>
        </DialogHeader>
        <div className="mt-6">
          <Form {...form} schema={baseSchema}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="label"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('games.create.label')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('games.create.label.placeholder')}
                        {...field}
                        error={!!fieldState.error}
                        disabled={!canEditLabel}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('games.create.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('games.create.description.placeholder')}
                        {...field}
                        error={!!fieldState.error}
                        disabled={!canEditDescription}
                        rows={4}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="openingDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('games.create.opening.date')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                              disabled={!canEditOpeningDate}
                            >
                              {field.value ? (
                                format(field.value, 'PPP')
                              ) : (
                                <span>{t('games.create.date.pick')}</span>
                              )}
                              <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        {canEditOpeningDate && (
                          <PopoverContent className="p-0 w-auto" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value ?? undefined}
                              onSelect={field.onChange}
                              disabled={(date) => {
                                const startOfToday = new Date();
                                startOfToday.setHours(0, 0, 0, 0);
                                return closingDate ? date >= closingDate : date < startOfToday;
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        )}
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="closingDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('games.create.closing.date')}</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={'outline'}
                              className={cn(
                                'pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'PPP')
                              ) : (
                                <span>{t('games.create.date.pick')}</span>
                              )}
                              <CalendarIcon className="ml-auto w-4 h-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        {canEditClosingDate && (
                          <PopoverContent className="p-0 w-auto" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value ?? undefined}
                              onSelect={field.onChange}
                              disabled={(date) => {
                                const startOfToday = new Date();
                                startOfToday.setHours(0, 0, 0, 0);
                                return openingDate ? date <= openingDate : date < startOfToday;
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        )}
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="link"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('games.create.link')}</FormLabel>
                      <FormControl>
                        <Input
                          type="url"
                          placeholder={t('games.create.link.placeholder')}
                          {...field}
                          error={!!fieldState.error}
                          disabled={!canEditLink}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cover"
                  render={({ field: { value, onChange, ref, ...field }, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('games.create.cover.label')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            {...field}
                            onChange={(e) => {
                              if (canEditCover) {
                                const file = e.target.files?.[0];
                                if (file) {
                                  onChange(file);
                                }
                              }
                            }}
                            error={!!fieldState.error}
                            className="hidden"
                            disabled={!canEditCover}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              if (canEditCover) {
                                fileInputRef.current?.click();
                              }
                            }}
                            className="w-full"
                            disabled={!canEditCover}
                          >
                            <UploadIcon className="flex-shrink-0 mr-2 w-4 h-4" />
                            <span className="truncate">
                              {value ? value.name : t('games.create.cover.button')}
                            </span>
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <EntitySegmentsCard key={game.id} entityType="game" entityId={game.id} />

              <Button rounded="full" type="submit" loading={updateGameMutation.isPending}>
                {t('games.update.submit')}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
